import type { BandRole, BandStats, RoleSource, Warning } from './types';

export interface RoleHit { role: BandRole; why: string }

/**
 * Read a role out of a band description.
 *
 * Band-number codes are mission-specific and the collision is real: SR_B5 is NIR on
 * Landsat, B05 is red edge on Sentinel-2. A bare "B5" with no mission prefix is left
 * unmatched rather than guessed, because guessing would be wrong about half the time.
 */
export function roleFromText(raw: string | null | undefined): RoleHit | null {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();

  const landsat = s.match(/^(?:sr_|st_|toa_)b(\d{1,2})$/);
  if (landsat) {
    const L: Record<number, BandRole> = {
      1: 'coastal', 2: 'blue', 3: 'green', 4: 'red', 5: 'nir', 6: 'swir16', 7: 'swir22',
    };
    const r = L[+(landsat[1] as string)];
    if (r) return { role: r, why: 'Landsat SR_B band number' };
  }
  if (/\bb8a\b/.test(s)) return { role: 'nir08', why: 'Sentinel-2 band code' };
  const s2 = s.match(/\bb(0\d|1[0-2])\b/); // zero-padded implies Sentinel-2
  if (s2) {
    const S: Record<number, BandRole> = {
      1: 'coastal', 2: 'blue', 3: 'green', 4: 'red', 5: 'rededge1', 6: 'rededge2',
      7: 'rededge3', 8: 'nir', 9: 'watervapour', 10: 'cirrus', 11: 'swir16', 12: 'swir22',
    };
    const r = S[+(s2[1] as string)];
    if (r) return { role: r, why: 'Sentinel-2 band code' };
  }

  // Order matters: thermal before swir (LWIR contains "wir"), red edge before red.
  const KW: [RegExp, BandRole][] = [
    [/ndvi|ndre|ndwi|ndmi|savi|evi|gndvi|\bindex\b/, 'index'],
    [/alpha|opacity|transparen/, 'alpha'],
    [/lwir|tirs|\btir\b|thermal|brightness.?temp|\blst\b|\bst_b\d/, 'thermal'],
    [/swir.?2|2190|2200|swir22/, 'swir22'],
    [/swir|1610|1600/, 'swir16'],
    [/cirrus|1375/, 'cirrus'],
    [/water.?vapou?r|945/, 'watervapour'],
    [/narrow.?nir|nir.?08|865/, 'nir08'],
    [/red.?edge|rededge|\brdg\b|\bre\b|705|717|740|783/, 'rededge'],
    [/\bnir\b|near.?infra|842|840|\bn\b/, 'nir'],
    [/\bred\b|665|\br\b/, 'red'],
    [/\bgreen\b|560|\bg\b/, 'green'],
    [/\bblue\b|490|\bb\b/, 'blue'],
    [/coastal|aerosol|443/, 'coastal'],
    [/\bpan\b|panchro/, 'pan'],
  ];
  for (const [re, role] of KW) if (re.test(s)) return { role, why: 'band description' };
  return null;
}

export interface GuessInput {
  samples: number;
  photometric: number | undefined;
  alphaFlags: boolean[];
  descriptions: (string | null)[];
  sampleFormat: number;
  stats: BandStats[];
}
export interface GuessedBand { role: BandRole; source: RoleSource; why: string }

export function guessBands(input: GuessInput): { bands: GuessedBand[]; notes: Warning[] } {
  const { photometric, alphaFlags, descriptions, sampleFormat, stats } = input;
  const out: (GuessedBand | { role: null })[] = descriptions.map((d) => {
    const hit = roleFromText(d);
    return hit ? { role: hit.role, source: 'metadata' as RoleSource, why: hit.why } : { role: null };
  });
  alphaFlags.forEach((isA, i) => {
    if (isA && !out[i]!.role) {
      out[i] = { role: 'alpha', source: 'inferred', why: 'ExtraSamples marks this as alpha' };
    }
  });

  const notes: Warning[] = [];
  const unresolved = out.some((b) => !b.role);
  const namedFromMeta = out.filter((b) => (b as GuessedBand).source === 'metadata').length;

  if (unresolved && namedFromMeta > 0) {
    // A positional plan would align its Nth entry with the Nth band regardless of what
    // the named ones already claimed — an Altum-PT's LWIR lands on entry 7 of a 7-band
    // plan and comes out as SWIR 2.2um.
    notes.push({ level: 'warn', text:
      `${namedFromMeta} of ${out.length} bands are named in the file and the rest are not. ` +
      'The unnamed ones are left **unassigned** rather than guessed by position, which ' +
      'would silently mislabel them. Set them by hand below.' });
  }

  if (unresolved && namedFromMeta === 0) {
    // Count data bands only: a 5-band camera written with an alpha band is still a
    // 5-band camera, and counting the alpha would shift every role by one.
    const isAlpha = out.map((b, i) => b.role === 'alpha' || alphaFlags[i]);
    const dataIdx = out.map((_, i) => i).filter((i) => !isAlpha[i]);
    const n = dataIdx.length;
    let plan: BandRole[] | null = null;
    let src: RoleSource = 'guess';
    let why = 'band-count convention';

    if (n === 1) {
      const s0 = stats[dataIdx[0] as number];
      const isIdx = sampleFormat === 3 && s0 && s0.min < -0.05;
      plan = [isIdx ? 'index' : 'pan']; src = 'inferred';
      why = isIdx ? 'single float band with negative values' : 'single band';
    } else if (n === 3) {
      plan = ['red', 'green', 'blue'];
      if (photometric === 2) { src = 'inferred'; why = 'PhotometricInterpretation = RGB'; }
    } else if (n === 4) {
      plan = ['red', 'green', 'blue', 'nir'];
      notes.push({ level: 'warn', text:
        'Four data bands with no alpha flag — assumed **R, G, B, NIR** (NAIP / PlanetScope / ' +
        'most 4-band drone output). If it is really RGBA the fourth band will be a flat 255 ' +
        'and NDVI will come out near-constant.' });
    } else if (n === 5) {
      plan = ['blue', 'green', 'red', 'nir', 'rededge'];
      notes.push({ level: 'bad', text:
        'Five bands is the dangerous case. MicaSense RedEdge writes **B,G,R,NIR,RedEdge**; ' +
        'DJI P4 Multispectral writes **B,G,R,RedEdge,NIR** — same count, bands 4 and 5 ' +
        'swapped. Guessing wrong silently trades NDVI for NDRE. Confirm bands 4 and 5 from ' +
        'the thumbnails: over vegetation, NIR is markedly brighter than red edge.' });
    } else if (n === 6) plan = ['blue', 'green', 'red', 'rededge', 'nir', 'swir16'];
    else if (n === 7) plan = ['coastal', 'blue', 'green', 'red', 'nir', 'swir16', 'swir22'];
    else if (n === 12) plan = ['coastal', 'blue', 'green', 'red', 'rededge1', 'rededge2',
      'rededge3', 'nir', 'nir08', 'watervapour', 'swir16', 'swir22'];
    else if (n === 13) plan = ['coastal', 'blue', 'green', 'red', 'rededge1', 'rededge2',
      'rededge3', 'nir', 'nir08', 'watervapour', 'cirrus', 'swir16', 'swir22'];

    if (plan) {
      dataIdx.forEach((bi, k) => {
        const role = plan![k];
        if (!out[bi]!.role && role) out[bi] = { role, source: src, why };
      });
    }
  }

  const bands: GuessedBand[] = out.map((b) =>
    b.role ? (b as GuessedBand) : { role: 'unassigned', source: 'guess', why: 'no signal' });

  // A hint for the eye, never an assignment.
  const cand = bands.map((b, i) => ({ i, b, s: stats[i] }))
    .filter((x) => x.b.role !== 'alpha' && x.b.source !== 'metadata' && x.s && !x.s.empty);
  if (cand.length >= 2) {
    const sorted = [...cand].sort((a, b) => b.s!.mean - a.s!.mean);
    const med = [...cand].sort((a, b) => a.s!.mean - b.s!.mean)[Math.floor(cand.length / 2)]!;
    const top = sorted[0]!;
    if (med.s!.mean > 0 && top.s!.mean > med.s!.mean * 1.4) {
      notes.push({ level: 'info', hint: true, text:
        `Band ${top.i + 1} is the brightest unassigned band (mean ${top.s!.mean.toFixed(4)} vs ` +
        `${med.s!.mean.toFixed(4)} median). Over vegetated ground that is what NIR looks like. ` +
        'Treat it as a nudge, not proof.' });
    }
  }
  return { bands, notes };
}
